import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronUp, Ruler, Info } from 'lucide-react';
import { Card } from '../components/ui/card';
import Footer from '../components/Footer';

const SizeGuidePage = () => {
  const [openSection, setOpenSection] = useState('tops');

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  const SizeTable = ({ headers, rows, highlight }) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-black text-white">
            {headers.map((header, i) => (
              <th key={i} className="px-3 py-3 text-left font-semibold whitespace-nowrap">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr 
              key={i} 
              className={`border-b border-gray-100 ${
                row.highlight ? 'bg-neutral-50' : i % 2 === 0 ? 'bg-white' : 'bg-gray-50'
              } hover:bg-neutral-50 transition-colors`}
            >
              {row.cells.map((cell, j) => (
                <td key={j} className={`px-3 py-3 ${j === 0 ? 'font-medium text-gray-800' : 'text-gray-600'}`}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const Section = ({ id, icon, title, children }) => (
    <Card className="overflow-hidden">
      <button
        onClick={() => toggleSection(id)}
        className="w-full flex items-center justify-between p-4 bg-white hover:bg-gray-50 transition-colors"
        data-testid={`section-${id}`}
      >
        <div className="flex items-center gap-3">
          <span className="text-2xl">{icon}</span>
          <h2 className="text-lg font-bold text-gray-800">{title}</h2>
        </div>
        {openSection === id ? (
          <ChevronUp className="w-5 h-5 text-gray-500" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-500" />
        )}
      </button>
      {openSection === id && (
        <div className="border-t border-gray-100">
          {children}
        </div>
      )}
    </Card>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-black text-white">
        <div className="container mx-auto px-4 py-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-4 transition"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver a la tienda
          </Link>
          <div className="flex items-center gap-3">
            <Ruler className="w-8 h-8" />
            <div>
              <h1 className="text-3xl font-bold">Guía de Tallas</h1>
              <p className="text-white/80">Encuentra tu talla perfecta</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Tips Box */}
        <Card className="p-4 mb-6 bg-neutral-50 border-neutral-200">
          <div className="flex gap-3">
            <Info className="w-5 h-5 text-black flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-gray-800 mb-1">¿Cómo medir?</h3>
              <p className="text-sm text-gray-600">
                Usa una cinta métrica flexible. Mide tu <strong>pecho</strong> (parte más ancha), 
                <strong> cintura</strong> (parte más estrecha) y <strong>cadera</strong> (parte más ancha de la cadera).
              </p>
            </div>
          </div>
        </Card>

        <div className="space-y-4">
          {/* Tops y Camisetas */}
          <Section id="tops" icon="👕" title="Tops y Camisetas">
            <div className="p-4">
              <h3 className="font-semibold text-black mb-3">👩 Mujer – Top / Camiseta</h3>
              <SizeTable 
                headers={['Talla', 'Pecho (cm)', 'Cintura (cm)', 'Largo (cm)', 'Ajuste']}
                rows={[
                  { cells: ['XS', '78–84', '60–66', '55–58', 'Ajustado, elástico'] },
                  { cells: ['S', '85–90', '67–72', '58–60', 'Fit estándar'] },
                  { cells: ['M', '91–96', '73–78', '60–62', 'Más demandada'], highlight: true },
                  { cells: ['L', '97–104', '79–86', '62–64', 'Holgado'] },
                  { cells: ['XL', '105–112', '87–94', '64–66', 'Oversize'] },
                ]}
              />
            </div>
            <div className="p-4 border-t border-gray-100">
              <h3 className="font-semibold text-black mb-3">👨 Hombre – Camiseta Oversize</h3>
              <SizeTable 
                headers={['Talla', 'Pecho (cm)', 'Cintura (cm)', 'Largo (cm)', 'Ajuste']}
                rows={[
                  { cells: ['S', '88–94', '74–80', '68–70', 'Ligero oversize'] },
                  { cells: ['M', '95–101', '81–87', '70–72', 'Oversize clásico'], highlight: true },
                  { cells: ['L', '102–108', '88–94', '72–74', 'Oversize amplio'] },
                  { cells: ['XL', '109–115', '95–101', '74–76', 'Relaxed fit'] },
                  { cells: ['XXL', '116–122', '102–108', '76–78', 'Muy holgado'] },
                ]}
              />
            </div>
          </Section>

          {/* Leggings y Pantalones */}
          <Section id="leggings" icon="🩳" title="Leggings, Shorts y Enterizos">
            <div className="p-4">
              <h3 className="font-semibold text-black mb-3">👩 Mujer – Leggings / Shorts / Enterizo</h3>
              <SizeTable 
                headers={['Talla', 'Cintura (cm)', 'Cadera (cm)', 'Largo (cm)', 'Ajuste']}
                rows={[
                  { cells: ['XS', '60–66', '84–90', '90–92', 'Compresivo'] },
                  { cells: ['S', '67–72', '91–96', '92–94', 'Cómodo'] },
                  { cells: ['M', '73–78', '97–102', '94–96', 'Clásico'], highlight: true },
                  { cells: ['L', '79–86', '103–110', '96–98', 'Holgado'] },
                  { cells: ['XL', '87–94', '111–118', '98–100', 'Relajado'] },
                  { cells: ['XXL', '95–102', '119–126', '100–102', 'Plus size'] },
                ]}
              />
            </div>
            <div className="p-4 border-t border-gray-100">
              <h3 className="font-semibold text-black mb-3">👨 Hombre – Pantalón Corto</h3>
              <SizeTable 
                headers={['Talla', 'Cintura (cm)', 'Cadera (cm)', 'Largo (cm)', 'Ajuste']}
                rows={[
                  { cells: ['S', '74–80', '90–96', '40–42', 'Cómodo'] },
                  { cells: ['M', '81–87', '97–102', '42–44', 'Estándar'], highlight: true },
                  { cells: ['L', '88–94', '103–108', '44–46', 'Holgado'] },
                  { cells: ['XL', '95–101', '109–114', '46–48', 'Relajado'] },
                  { cells: ['XXL', '102–108', '115–120', '48–50', 'Muy holgado'] },
                ]}
              />
            </div>
          </Section>

          {/* Chaquetas */}
          <Section id="chaquetas" icon="🧥" title="Chaquetas y Abrigos">
            <div className="p-4">
              <SizeTable 
                headers={['Talla', 'Pecho (cm)', 'Cintura (cm)', 'Largo (cm)', 'Ajuste']}
                rows={[
                  { cells: ['XS', '78–84', '60–66', '62–64', 'Ajustado'] },
                  { cells: ['S', '85–90', '67–72', '64–66', 'Clásico'] },
                  { cells: ['M', '91–96', '73–78', '66–68', 'Estándar'], highlight: true },
                  { cells: ['L', '97–104', '79–86', '68–70', 'Holgado'] },
                  { cells: ['XL', '105–112', '87–94', '70–72', 'Oversize'] },
                  { cells: ['XXL', '113–120', '95–102', '72–74', 'Muy holgado'] },
                ]}
              />
            </div>
          </Section>

          {/* Sets */}
          <Section id="sets" icon="🎽" title="Sets Completos">
            <div className="p-4">
              <div className="bg-neutral-50 rounded-lg p-4">
                <p className="text-gray-700 mb-3">
                  Nuestros sets (top + leggings + chaqueta / enterizos) tienen <strong>ajuste elástico y compresivo</strong>, 
                  diseñados para máxima comodidad y libertad de movimiento.
                </p>
                <div className="flex items-start gap-2 text-sm text-gray-600">
                  <span className="text-black">💡</span>
                  <p>Si dudas entre dos tallas, <strong>elige tu talla de siempre</strong>.</p>
                </div>
              </div>
            </div>
          </Section>

          {/* Accesorios */}
          <Section id="accesorios" icon="🧦" title="Accesorios">
            <div className="p-4">
              <SizeTable 
                headers={['Producto', 'Talla', 'Notas']}
                rows={[
                  { cells: ['Gorra', 'Única ajustable', 'Cierre trasero ajustable'] },
                  { cells: ['Calcetines', 'Única elástica', 'Se adaptan a tallas 36-44'] },
                  { cells: ['Bolsa deportiva', 'Estándar', 'Apta para todos'] },
                  { cells: ['Felpa de pelo', 'Única oversize', 'Cómoda y holgada'] },
                ]}
              />
            </div>
          </Section>
        </div>

        {/* Consejos */}
        <Card className="mt-8 p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            📏 Consejos Prácticos
          </h2>
          <div className="space-y-4 text-gray-600">
            <div className="flex items-start gap-3">
              <span className="text-black font-bold">1.</span>
              <p>Mide tu <strong>pecho, cintura y cadera</strong> con cinta métrica flexible.</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-black font-bold">2.</span>
              <p>Compara tus medidas con la tabla antes de elegir talla.</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-black font-bold">3.</span>
              <p>Para prendas compresivas (leggings, enterizos, tops ajustados), el material permite adaptarse <strong>±2 cm</strong>.</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-black font-bold">4.</span>
              <div>
                <p className="font-medium text-gray-800 mb-1">Si dudas entre dos tallas:</p>
                <ul className="list-disc list-inside ml-2 space-y-1">
                  <li><strong>Tops / Chaquetas:</strong> elige la talla más grande para look relajado</li>
                  <li><strong>Leggings / Shorts:</strong> elige la talla más grande para comodidad</li>
                </ul>
              </div>
            </div>
          </div>
        </Card>

        {/* CTA */}
        <div className="mt-8 text-center">
          <p className="text-gray-600 mb-4">¿Sigues con dudas sobre tu talla?</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="https://wa.me/34611871722?text=Hola,%20necesito%20ayuda%20con%20mi%20talla"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition font-medium"
            >
              💬 Consultar por WhatsApp
            </a>
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-black text-white rounded-full hover:opacity-90 transition font-medium"
            >
              🛍️ Ir a la tienda
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SizeGuidePage;
